import * as React from 'react';
import {
    getReactAccordion,
    getReactButton,
    getReactCheckbox,
    getReactHierarchicalList, getReactInput,
    getReactLink,
    ReactInput
} from "../../index";
import {RefObject, useCallback} from "react";
import {ReactButtonState} from "./ReactButton";
import {ReactInputData} from "./ReactInput";
import {ReactTableData, ReactTableState} from "./ReactTable";
import {ReactLinkState} from "./ReactLink";

export interface HierarchicalCellData {
    className?: string,
    style?: {},
    displayValue?: any,
    displayOverride?: (rowObj: any, hierarchyIndex: number, disabled: boolean) => {},
    row?: any,
    hierarchyIndex?: number,
    title?: string,
    disabled?: boolean,
}

class ReactHierarchicalCell extends React.Component<HierarchicalCellData> {
    constructor(props: HierarchicalCellData) {
        super(props);
    }

    getCell() {
        if((null != this.props.displayOverride)) {
            return this.props.displayOverride(this.props.row, this.props.hierarchyIndex, this.props.disabled);
        } else if(this.props.title) {
            return <div className={this.props.className} style={this.props.style} title={this.props.title}>{this.props.displayValue}</div>;
        } else {
            return <div className={this.props.className} style={this.props.style}>{this.props.displayValue}</div>;
        }
    }

    render() {
        return this.getCell();
    }
}

interface ReactHierarchicalRowData {
    row: any,
    id?: string,
    hierarchyIndex?: number,
    rowClass?: string,
    rowStyle?: {},
    deleteButtonTabIndex?: null,
    sortColumnIndex?: number, // -1 to add to the end
    addSelectColumn?: boolean,
    selectColumnIndex?: number, // -1 to add to the end
    onRowClick?: (rowOBJ: any, shiftKey: boolean, ctrlKey: boolean, hierarchyIndex: number, isRowClick: boolean) => void,
    onRowRightClick?: (rowOBJ: any, shiftKey: boolean, ctrlKey: boolean, hierarchyIndex: number) => void,
    deleteColumnIndex?: number, // -1 to add to the end
    onDelete?: (rowOBJ: any, shiftKey: boolean, ctrlKey: boolean, hierarchyIndex: number) => void,
    selectColumns?: [],
    css?: string,
    style?: {},
    selectRecordWithRowClick?: boolean,
    displayOverride?: (rowObj: any, hierarchyIndex: number, disabled: boolean) => {},
    displayValue?: string,
    stopPropagationOnClick?: boolean,
    hightlightSelectedRowCSS?: (row: any, hierarchyIndex: number) => string,
    rowAdditionalAttributes?: (row: any, hierarchyIndex: number) => {},
    doHighlightRow?: boolean,
    tabIndex?: number,
    rowReference?: RefObject<any>,
    disabled?: boolean,
    addDeleteButton?: boolean,
    addSortIcon?: boolean,
    addDropIcon?: boolean,
    deleteButtonID?: string,
    shouldRowUpdate?: (nextRow: any, row: any, rowIndex: number) => boolean,
}

class ReactHierarchicalRow extends React.Component<ReactHierarchicalRowData> {
    constructor(props: ReactHierarchicalRowData) {
        super(props);

        // Binding is necessary to allow these functions access to the State data
        this.onRowClick = this.onRowClick.bind(this);
        this.onRowRightClick = this.onRowRightClick.bind(this);
        this.onDelete = this.onDelete.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<ReactHierarchicalRowData>, nextState: Readonly<{}>, nextContext: any): boolean {
        if(null != this.props.shouldRowUpdate) {
            return this.props.shouldRowUpdate(nextProps.row, this.props.row, nextProps.hierarchyIndex);
        } else {
            return nextProps.row != this.props.row ||
                nextProps.doHighlightRow != this.props.doHighlightRow ||
                nextProps.disabled != this.props.disabled ||
                nextProps.css != this.props.css ||
                nextProps.style != this.props.style ||
                nextProps.rowClass != this.props.rowClass ||
                nextProps.rowStyle != this.props.rowStyle
                ;
        }
    }

    onRowClick(event: any) {
        if(null != this.props.onRowClick) {
            this.props.onRowClick(this.props.row, event.shiftKey, event.ctrlKey, this.props.hierarchyIndex, true);
        }
        if(this.props.stopPropagationOnClick) {
            event.stopPropagation();
        }
    }

    onRowRightClick(event: any) {
        event.preventDefault();

        if(null != this.props.onRowRightClick) {
            this.props.onRowRightClick(this.props.row, event.shiftKey, event.ctrlKey, this.props.hierarchyIndex);
        }
    }

    onDelete(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        if(null != this.props.onDelete) {
            this.props.onDelete(this.props.row, event.shiftKey, event.ctrlKey, this.props.hierarchyIndex);
        }
        event.stopPropagation();
    }

    getDeleteCell(id: string) {
        return <div id={(id + '_delete')} className={(this.props.addDeleteButton) ? "align-obj-center" : "align-obj-center categoryAllHide"}>
            <button id={(this.props.deleteButtonID.length > 0) ? (this.props.deleteButtonID + '_' + this.props.hierarchyIndex) : (id + '_deleteButton')} tabIndex={this.props.deleteButtonTabIndex} className={"ui-button ui-corner-all ui-widget ui-button-icon-only" + ((this.props.disabled) ? " ui-button-disabled ui-state-disabled" : "")} onClick={this.onDelete} type="button" disabled={this.props.disabled} >
                <span className="ui-button-icon ui-icon ui-icon-circle-close"></span>
                <span className="ui-button-icon-space"> </span>
            </button>
        </div>;
    }

    getSortCell(id: string) {
        return <div id={(id + '_sort')} className={(this.props.addSortIcon) ? "align-obj-center" : "align-obj-center categoryAllHide"}>
            <i className={'fas fa-grip-lines ui-sortable movable manualSort' + ((this.props.disabled) ? " ui-button-disabled ui-state-disabled" : "")} ></i>
        </div>;
    }

    getDropCell(id: string) {
        return <div id={(id + '_drop')} className={(this.props.addDropIcon) ? "align-obj-center" : "align-obj-center categoryAllHide"}>
            <i className={'fas fa-reply ui-sortable movable manualSort' + ((this.props.disabled) ? " ui-button-disabled ui-state-disabled" : "")} ></i>
        </div>;
    }

    render() {
        const rowHighlightClass = (null != this.props.hightlightSelectedRowCSS) ? this.props.hightlightSelectedRowCSS(this.props.row, this.props.hierarchyIndex) : 'copia_tableHighlightColor_b nowrap';
        const rowClasses = (this.props.doHighlightRow) ? (' ' + rowHighlightClass) : '';
        const rowAdditionalAttributes = (null != this.props.rowAdditionalAttributes) ? this.props.rowAdditionalAttributes(this.props.row, this.props.hierarchyIndex) : {};

        return <div id={this.props.id} ref={this.props.rowReference} style={this.props.style} className={this.props.css + rowClasses} onClick={this.onRowClick} onContextMenu={this.onRowRightClick} {...rowAdditionalAttributes}>
            <ReactHierarchicalCell
                row={this.props.row}
                hierarchyIndex={this.props.hierarchyIndex}
                displayValue={this.props.displayValue}
                displayOverride={this.props.displayOverride}
                style={this.props.rowStyle}
                className={this.props.rowClass}
                disabled={this.props.disabled}
            />
            {this.getSortCell(this.props.id)}
            {this.getDropCell(this.props.id)}
            {this.getDeleteCell(this.props.id)}
        </div>;
    }
}


interface ReactHierarchicalListData {
    id?: string,
    hierarchyKey?: string,
    style?: {},
    css?: string,
    divContainerCSS?: string,
    divContainerStyle?: {},
    collapsedStyle?: {},
    collapsedCSS?: string,
    expandedCSS?: string,
    expandedStyle?: {},
    contentStyle?: (row: any, rowIndex: number) => {},
    contentCSS?: string,
    dataKey: string,
    rows: any[], // All Rows
    tabIndex?: null,
    refreshID?: string,
    onRefresh?: () => {},
    expandedID?: string,
    stopPropagationOnClick?: boolean,
    disabled?: boolean,
    disableID?: string,
    rowID?: string,
    deleteButtonID?: string,
    totalCountID?: string,
    arrowExpandCSS?: string,
    arrowCollapseCSS?: string,
    arrowStyle?:  (row: any, rowIndex: number) => {},
    displayOverride: (rowObj: any, hierarchyIndex: number, disabled: boolean) => {},
    contentOverride: (rowObj: any, hierarchyIndex: number, disabled: boolean) => {},
    displayKey: string,
    isSelectedKey: string,
    highlightOnClick?: boolean,
    hightlightSelectedRowCSS?: (row: any, hierarchyIndex: number) => string,
    rowAdditionalAttributes?: (row: any, hierarchyIndex: number) => {},
    rowsCSS?: (row: any, rowIndex: number) => string,
    rowsStyle?: (row: any, rowIndex: number) => {},
    onRowClick?: (rowObj: any, shiftKey: boolean, ctrlKey: boolean, hierarchyIndex: number, parentRowObj: any, indexInParent: number) => boolean,
    onRowRightClick?: (rowOBJ: any, shiftKey: boolean, ctrlKey: boolean, hierarchyIndex: number, parentRowObj: any, indexInParent: number) => boolean,
    onRowDelete?: (rowObj: any, shiftKey: boolean, ctrlKey: boolean, hierarchyIndex: number, parentRowObj: any, indexInParent: number) => boolean,
    onAfterRowDelete?: () => void,
    onKeyDown?: (rowOBJ: any, key: string, hierarchyIndex: number) => void,
    addDeleteButton?: boolean,
    addSortIcon?: boolean,
    addDropIcon?: boolean,
    deleteButtonTabIndex?: null,
    enableDragAndDrop?: boolean,
    allowDrag?: (rowObj: any, parentObj: any) => boolean,
    allowDrop?: (rowObj: any, parentObj: any) => boolean,
    onDropEvent?: (event: any) => void,
    enableUnboundDragAndDrop?: boolean,
    allowDropToTheBottom?: boolean,
    allowExpandAndCollapse?: (row: any, rowIndex: number) => boolean,
    shouldRowUpdate?: (nextRow: any, row: any, rowIndex: number) => boolean,
}

interface ReactHierarchicalListState {
    rows: any[],
    highlightSelectedRow: any,
    disabled: boolean,
    rowCount: number,
    rowIsDragging: boolean,
}

export class ReactHierarchicalList extends React.Component<ReactHierarchicalListData> {
    totalRows: any[];
    foundPrevRow: boolean;
    returnNextRow: boolean;
    foundParentRow: boolean;
    // dragRowObj: JQuery;
    highlightRowRef: RefObject<any>;

    constructor(props: ReactHierarchicalListData) {
        super(props);

        this.highlightRowRef = React.createRef();
        this.totalRows = [];
        this.foundPrevRow = false;
        this.returnNextRow = false;
        this.foundParentRow = false;

        this.state = {
            rows: props.rows,
            highlightSelectedRow: null,
            disabled: props.disabled,
            rowIsDragging: false,
        };

        // Binding is necessary to allow these functions access to the State data
        this.onRefresh = this.onRefresh.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onRightClick = this.onRightClick.bind(this);
        this.onDisableClick = this.onDisableClick.bind(this);
        this.onDrop = this.onDrop.bind(this);
        this.onDragHandler = this.onDragHandler.bind(this);
        this.onDragStartHandler = this.onDragStartHandler.bind(this);
    }

    // Update the state externally
    componentWillReceiveProps(props: ReactHierarchicalListData) {
        this.totalRows = [];
        this.foundPrevRow = false;
        this.returnNextRow = false;
        this.foundParentRow = false;

        this.setState({
            rows: props.rows,
            highlightSelectedRow: null,
        });
    }

    selectRow(rows: any[], row: any) {
        for(let i = 0; i < rows.length; i++) {
            rows[i][this.props.isSelectedKey] = (rows[i] == row);

            if(null != rows[i][this.props.hierarchyKey] && rows[i][this.props.hierarchyKey].length > 0) {
                this.selectRow(rows[i][this.props.hierarchyKey], row);
            }
        }
    }

    getParentRow(rows: any[], row: any, parentRow: any):any {
        for(let i = 0; i < rows.length; i++) {
            if(this.foundParentRow) {
                return parentRow;
            } else if(row == null) {
                return rows[i];
            } else if(rows[i] == row) {
                this.foundParentRow = true;
                return parentRow;
            }

            if(null != rows[i][this.props.hierarchyKey] && rows[i][this.props.hierarchyKey].length > 0) {
                const tempParent = this.getParentRow(rows[i][this.props.hierarchyKey], row, rows[i]);
                if(this.foundParentRow) {
                    return tempParent;
                }
            }
        }

        return parentRow;
    }

    getPrevRow(rows: any[], row: any, previousRow: any):any {
        const dataKey = (null != this.props.dataKey) ? this.props.dataKey : "isExpanded";

        for(let i = 0; i < rows.length; i++) {
            if(this.foundPrevRow) {
                return previousRow;
            } else if(row == null) {
                return rows[i];
            } else if(rows[i] == row) {
                this.foundPrevRow = true;
                return previousRow;
            }

            previousRow = rows[i];

            if(null != rows[i][dataKey] && rows[i][dataKey] && null != rows[i][this.props.hierarchyKey] && rows[i][this.props.hierarchyKey].length > 0) {
                previousRow = this.getPrevRow(rows[i][this.props.hierarchyKey], row, previousRow);
            }
        }
        return previousRow;
    }

    getNextRow(rows: any[], row: any):any {
        const dataKey = (null != this.props.dataKey) ? this.props.dataKey : "isExpanded";
        for(let i = 0; i < rows.length; i++) {
            if(null == row) {
                return rows[i];
            } else if(rows[i] == row) {
                this.returnNextRow = true;
            } else if(this.returnNextRow) {
                return rows[i];
            }

            if(null != rows[i][dataKey] && rows[i][dataKey] && null != rows[i][this.props.hierarchyKey] && rows[i][this.props.hierarchyKey].length > 0) {
                const nextRow = this.getNextRow(rows[i][this.props.hierarchyKey], row);
                if(null != nextRow) {
                    return nextRow;
                }
            }
        }
        return null;
    }

    onRightClick(row: any, shiftKey: boolean, ctrlKey: boolean, hierarchyIndex: number) {
        if(this.props.highlightOnClick) {
            this.setState(() => {
                this.selectRow((this.state as ReactHierarchicalListState).rows, row);

                return {
                    rows: (this.state as ReactHierarchicalListState).rows,
                    highlightSelectedRow: row
                }
            });
        }

        if(null != this.props.onRowRightClick) {
            this.foundParentRow = false;
            const parentRow = this.getParentRow((this.state as ReactHierarchicalListState).rows, row, null);
            let indexInParent = -1;
            if(null != parentRow && null != parentRow[this.props.hierarchyKey] && parentRow[this.props.hierarchyKey].length > 0) {
                indexInParent = parentRow[this.props.hierarchyKey].indexOf(row);
            }

            this.props.onRowRightClick(row, shiftKey, ctrlKey, hierarchyIndex, parentRow, indexInParent);
        }
    }

    onClick(row: any, shiftKey: boolean, ctrlKey: boolean, hierarchyIndex: number, isRowClick?: boolean) {
        if(this.props.highlightOnClick && (null != isRowClick && isRowClick)) {
            this.setState(() => {
                this.selectRow((this.state as ReactHierarchicalListState).rows, row);

                return {
                    rows: (this.state as ReactHierarchicalListState).rows,
                    highlightSelectedRow: row,
                    rowIsDragging: false,
                }
            });

            return false;
        } else {
            if(null != this.props.onRowClick) {
                this.foundParentRow = false;
                const parentRow = this.getParentRow((this.state as ReactHierarchicalListState).rows, row, null);
                let indexInParent = -1;
                if(null != parentRow && null != parentRow[this.props.hierarchyKey] && parentRow[this.props.hierarchyKey].length > 0) {
                    indexInParent = parentRow[this.props.hierarchyKey].indexOf(row);
                }
                return this.props.onRowClick(row, shiftKey, ctrlKey, hierarchyIndex, parentRow, indexInParent);
            } else {
                const dataKey = (null != this.props.dataKey) ? this.props.dataKey : "isExpanded";
                const isExpanded = null != row[dataKey] && row[dataKey];
                return !isExpanded;
            }
        }
    }

    onDelete(row: any, shiftKey: boolean, ctrlKey: boolean, hierarchyIndex: number) {
        this.foundParentRow = false;
        const parentRow = this.getParentRow((this.state as ReactHierarchicalListState).rows, row, null);
        let indexInParent = -1;
        if(null != parentRow && null != parentRow[this.props.hierarchyKey] && parentRow[this.props.hierarchyKey].length > 0) {
            indexInParent = parentRow[this.props.hierarchyKey].indexOf(row);
        }

        let deleteRowAndAllChildren = true;
        if(null != this.props.onRowDelete) {
            deleteRowAndAllChildren = this.props.onRowDelete(row, shiftKey, ctrlKey, hierarchyIndex, parentRow, indexInParent);
        }

        if(deleteRowAndAllChildren) {
            if(this.foundParentRow) {
                if(indexInParent > -1) {
                    parentRow[this.props.hierarchyKey].splice(indexInParent, 1);
                } else {
                    const indexInRow = (this.state as ReactHierarchicalListState).rows.indexOf(row);
                    if(indexInRow > -1) {
                        (this.state as ReactHierarchicalListState).rows.splice(indexInRow, 1);
                    }
                }
                this.setState(() => {
                    return {
                        rows: (this.state as ReactHierarchicalListState).rows,
                        highlightSelectedRow: null,
                        rowIsDragging: false,
                    }
                },
            () => {
                    if (null != this.props.onAfterRowDelete) {
                        this.props.onAfterRowDelete();
                    }
                });
            }
        }
    }

    onDragHandler(event: any, dragOverRow: any) {
        if(null != (this.state as ReactHierarchicalListState).highlightSelectedRow && null != (this.state as ReactHierarchicalListState).highlightSelectedRow.allowDrag && (this.state as ReactHierarchicalListState).highlightSelectedRow.allowDrag && null != dragOverRow) {
            this.foundParentRow = false;
            const currentParentRow = this.getParentRow((this.state as ReactHierarchicalListState).rows, (this.state as ReactHierarchicalListState).highlightSelectedRow, null);

            const isHiddenDragRow = null != dragOverRow.isHiddenDragRow && dragOverRow.isHiddenDragRow;

            let allowDrag = false;
            let allowDrop = false;
            if(this.props.enableUnboundDragAndDrop && isHiddenDragRow) {
                if(null != dragOverRow.isBottomRow) { // If there is not parent then this is the bottom of the Hierarchy
                    allowDrop = this.props.allowDropToTheBottom;
                } else {
                    allowDrop = (null != dragOverRow.hiddenParentRow && null != dragOverRow.hiddenParentRow.allowDrop && dragOverRow.hiddenParentRow.allowDrop) && !this.isRelatedTo((this.state as ReactHierarchicalListState).highlightSelectedRow, dragOverRow.hiddenParentRow);
                }

                if(allowDrop) {
                    // Dragging over a hidden drag row
                    let currentIndex = -1;
                    if (null != currentParentRow) {
                        currentIndex = currentParentRow[this.props.hierarchyKey].indexOf((this.state as ReactHierarchicalListState).highlightSelectedRow);
                        if(currentIndex > -1) {
                            currentParentRow[this.props.hierarchyKey].splice(currentIndex, 1);
                        }
                    } else {
                        currentIndex = (this.state as ReactHierarchicalListState).rows.indexOf((this.state as ReactHierarchicalListState).highlightSelectedRow)
                        if(currentIndex > -1) {
                            (this.state as ReactHierarchicalListState).rows.splice(currentIndex, 1);
                        }
                    }

                    if(currentIndex > -1) {
                        if(null != dragOverRow.hiddenParentRow) {
                            // Drag as a child of the parent row
                            dragOverRow.hiddenParentRow[this.props.hierarchyKey].push((this.state as ReactHierarchicalListState).highlightSelectedRow);
                        } else {
                            // If the parent is null then add to the end of the base (parent most) list
                            (this.state as ReactHierarchicalListState).rows.push((this.state as ReactHierarchicalListState).highlightSelectedRow);
                        }
                    }
                }
            } else {
                let dragParentRow = null;
                // Check if the dragOverRow is a child of the parent element, Dont allow the parent object to be dragged into its own child
                if(this.isRelatedTo((this.state as ReactHierarchicalListState).highlightSelectedRow, dragOverRow)) {
                    dragParentRow = currentParentRow; // Prevent the parent from being dragged into its own child
                } else {
                    this.foundParentRow = false;
                    dragParentRow = this.getParentRow((this.state as ReactHierarchicalListState).rows, dragOverRow, null);
                }

                allowDrag = (currentParentRow == dragParentRow) && (null != dragOverRow.allowDrag && dragOverRow.allowDrag);

                if(null != dragParentRow) {
                    allowDrop = (null != dragParentRow.allowDrop && dragParentRow.allowDrop);
                } else {
                    allowDrop = (null != dragOverRow.allowDrop && dragOverRow.allowDrop);
                }

                if(allowDrop || allowDrag) {
                    // Check if we are dragging items on the same level
                    if(allowDrag) {
                        if (null != currentParentRow) {
                            const currentIndex = dragParentRow[this.props.hierarchyKey].indexOf((this.state as ReactHierarchicalListState).highlightSelectedRow);
                            const dragIndex = dragParentRow[this.props.hierarchyKey].indexOf(dragOverRow);
                            if (currentIndex > -1 && dragIndex > -1) {
                                dragParentRow[this.props.hierarchyKey].splice(currentIndex, 1);
                                dragParentRow[this.props.hierarchyKey].splice(dragIndex, 0, (this.state as ReactHierarchicalListState).highlightSelectedRow);
                            }
                        } else {
                            const currentIndex = (this.state as ReactHierarchicalListState).rows.indexOf((this.state as ReactHierarchicalListState).highlightSelectedRow)
                            const dragIndex = (this.state as ReactHierarchicalListState).rows.indexOf(dragOverRow)
                            if(currentIndex > -1 && dragIndex > -1) {
                                (this.state as ReactHierarchicalListState).rows.splice(currentIndex, 1);
                                (this.state as ReactHierarchicalListState).rows.splice(dragIndex, 0, (this.state as ReactHierarchicalListState).highlightSelectedRow);
                            }
                        }
                    } else if(this.props.enableUnboundDragAndDrop) { // Drag items to new levels
                        let currentIndex = -1;
                        if (null != currentParentRow) {
                            currentIndex = currentParentRow[this.props.hierarchyKey].indexOf((this.state as ReactHierarchicalListState).highlightSelectedRow);
                        } else {
                            currentIndex = (this.state as ReactHierarchicalListState).rows.indexOf((this.state as ReactHierarchicalListState).highlightSelectedRow)
                        }

                        let dragIndex = -1;
                        if (null != dragParentRow) {
                            dragIndex = dragParentRow[this.props.hierarchyKey].indexOf(dragOverRow);
                        } else {
                            dragIndex = (this.state as ReactHierarchicalListState).rows.indexOf(dragOverRow)
                        }

                        if (currentIndex > -1 && dragIndex > -1) {
                            if (null != currentParentRow) {
                                currentParentRow[this.props.hierarchyKey].splice(currentIndex, 1);
                            } else {
                                (this.state as ReactHierarchicalListState).rows.splice(currentIndex, 1);
                            }

                            if (null != dragParentRow) {
                                dragParentRow[this.props.hierarchyKey].splice(dragIndex, 0, (this.state as ReactHierarchicalListState).highlightSelectedRow);
                            } else {
                                (this.state as ReactHierarchicalListState).rows.splice(dragIndex, 0, (this.state as ReactHierarchicalListState).highlightSelectedRow);
                            }
                        }
                    }
                }
            }

            if(allowDrop || allowDrag) {
                this.setState(() => {
                    return {
                        rows: (this.state as ReactHierarchicalListState).rows,
                    }
                }, () => {
                    this.executeScroll();
                });
            }
        }
    }

    // Function to run when row is dropped
    onDrop(event: any) {
        // Call the onDropEvent function if it exists
        if(null != this.props.onDropEvent) {
            this.props.onDropEvent(event);
        }

        this.setState({
            rows: (this.state as ReactHierarchicalListState).rows,
            rowIsDragging: false
        });
    }

    onDragStartHandler(event: any, dragRow: any) {
        const isHiddenDragRow = null != dragRow.isHiddenDragRow && dragRow.isHiddenDragRow;
        if(!isHiddenDragRow && this.props.enableDragAndDrop && !(this.state as ReactHierarchicalListState).disabled) {
            if(null != dragRow.allowDrag && dragRow.allowDrag) {
                this.setState(() => {
                    this.selectRow((this.state as ReactHierarchicalListState).rows, dragRow);

                    return {
                        rows: (this.state as ReactHierarchicalListState).rows,
                        highlightSelectedRow: dragRow,
                        rowIsDragging: true,
                    }
                }, () => {
                    if(this.props.highlightOnClick) {
                        this.executeScroll();
                    }
                });
            }
        }
    }

    getHierarchyIndex(row: any):number {
        for(let i = 0; i < this.totalRows.length; i++) {
            if(this.totalRows[i] == row) {
                return i;
            }
        }
        return -1;
    }

    // Handle On Key Down Events
    onKeyDown(event: any) {
        const keyUp = event.key == "ArrowUp";
        const keyDown = event.key == "ArrowDown";
        const keyLeft = event.key == "ArrowLeft";
        const keyRight = event.key == "ArrowRight";
        const keyTab = event.key == "Tab";
        const keyEnter = event.key == "Enter";

        let keyPressed = false;
        if  (this.props.highlightOnClick && (keyUp || keyDown)) {
            // Move the Highlight Selected Row up and down
            keyPressed = true;

            let nextRow: any = null;
            if(keyUp) {
                this.foundPrevRow = false;
                nextRow = this.getPrevRow((this.state as ReactHierarchicalListState).rows, (this.state as ReactHierarchicalListState).highlightSelectedRow, null);
            } else {
                this.returnNextRow = false;
                nextRow = this.getNextRow((this.state as ReactHierarchicalListState).rows, (this.state as ReactHierarchicalListState).highlightSelectedRow);
            }

            if(null != nextRow) {
                this.setState(() => {
                    this.selectRow((this.state as ReactHierarchicalListState).rows, nextRow);

                    return {
                        rows: (this.state as ReactHierarchicalListState).rows,
                        highlightSelectedRow: nextRow,
                        rowIsDragging: false,
                    }
                }, () => {
                    if(this.props.highlightOnClick) {
                        this.executeScroll();
                    }
                });
            }
        } else if (this.props.highlightOnClick && (keyLeft || keyRight)) {
            if(this.getAllowExpandAndCollapse((this.state as ReactHierarchicalListState).highlightSelectedRow)) {

                // Expand / Collapse highlighted row
                const dataKey = (null != this.props.dataKey) ? this.props.dataKey : "isExpanded";
                if(keyLeft) {
                    if(null != (this.state as ReactHierarchicalListState).highlightSelectedRow[dataKey] || (this.state as ReactHierarchicalListState).highlightSelectedRow[dataKey]) {
                        this.setState(() => {
                            (this.state as ReactHierarchicalListState).highlightSelectedRow[dataKey] = false;

                            return {
                                rows: (this.state as ReactHierarchicalListState).rows,
                                highlightSelectedRow: (this.state as ReactHierarchicalListState).highlightSelectedRow,
                                rowIsDragging: false,
                            }
                        }, () => {
                            if(this.props.highlightOnClick) {
                                this.executeScroll();
                            }
                        });
                    }
                } else {
                    if(null == (this.state as ReactHierarchicalListState).highlightSelectedRow[dataKey] || !(this.state as ReactHierarchicalListState).highlightSelectedRow[dataKey]) {
                        this.setState(() => {
                            (this.state as ReactHierarchicalListState).highlightSelectedRow[dataKey] = true;

                            return {
                                rows: (this.state as ReactHierarchicalListState).rows,
                                highlightSelectedRow: (this.state as ReactHierarchicalListState).highlightSelectedRow,
                                rowIsDragging: false,
                            }
                        }, () => {
                            if(this.props.highlightOnClick) {
                                this.executeScroll();
                            }
                        });
                    }
                }
            }
        } else if(this.props.highlightOnClick) {
            // Execute Enter Command
            if(null != this.props.onKeyDown) {
                this.props.onKeyDown((this.state as ReactHierarchicalListState).highlightSelectedRow, event.key, this.getHierarchyIndex((this.state as ReactHierarchicalListState).highlightSelectedRow));
            }

            if(this.props.highlightOnClick) {
                this.executeScroll();
            }
        }

        if (keyPressed) {
            event.preventDefault();
        }
    }

    onRefresh() {
        if(null != this.props.onRefresh) {
            this.props.onRefresh();
        }
        this.totalRows = [];
        this.foundPrevRow = false;
        this.returnNextRow = false;
        this.foundParentRow = false;

        this.setState(() => ({
            rows: (this.state as ReactHierarchicalListState).rows,
            rowCount: -1
        }));
    }

    executeScroll() {
        // Scroll the highlighted selected row into view
        if(null != this.highlightRowRef.current && null != (this.state as ReactHierarchicalListState).highlightSelectedRow && this.props.highlightOnClick) {
            this.highlightRowRef.current.parentElement.scrollIntoView({block: "center", inline: "center"});
        }
    }

    isRelatedTo(row: any, childRow: any):any {
        if(null == childRow) {
          return false;
        } else if(row == childRow) {
            return true;
        } else if(null != row[this.props.hierarchyKey] && row[this.props.hierarchyKey].length > 0) {
            for (let i = 0; i < row[this.props.hierarchyKey].length; i++) {
                if(this.isRelatedTo(row[this.props.hierarchyKey][i], childRow)) {
                    return true;
                }
            }
        }

        return false;
    }

    getRowDisplay(row: any, hierarchyIndex: number, isHiddenDragRow: boolean) {
        if(isHiddenDragRow) {
            return <ReactHierarchicalRow
                id={(this.props.rowID.length > 0) ? this.props.rowID : this.props.id + '_row_' + hierarchyIndex}
                row={row}
                doHighlightRow={false}
                hierarchyIndex={hierarchyIndex}
                deleteButtonID={this.props.deleteButtonID}
             />;
        } else {
            const doHighlight = this.props.highlightOnClick && null != this.props.isSelectedKey && row[this.props.isSelectedKey];
            row.hierarchyIndex = hierarchyIndex;

            let rowClass = "";
            if(null != row.css && 'undefined' != row.css) {
                rowClass = row.css;
            } else if(null != this.props.rowsCSS) {
                rowClass = this.props.rowsCSS(row, hierarchyIndex);
            }

            let rowStyle = {};
            if(null != row.style && 'undefined' != row.style) {
                rowStyle = row.style;
            } else if(null != this.props.rowsStyle) {
                rowStyle = this.props.rowsStyle(row, hierarchyIndex);
            }

            return <ReactHierarchicalRow
                id={(null != row.id) ? row.id : ((this.props.rowID.length > 0) ? this.props.rowID + '_' + hierarchyIndex: this.props.id + '_row_' + hierarchyIndex)}
                doHighlightRow={doHighlight}
                hightlightSelectedRowCSS={this.props.hightlightSelectedRowCSS}
                rowAdditionalAttributes={this.props.rowAdditionalAttributes}
                row={row}
                css={rowClass}
                style={rowStyle}
                hierarchyIndex={hierarchyIndex}
                displayValue={row[this.props.displayKey]}
                displayOverride={this.props.displayOverride}
                onRowClick={this.onClick}
                onRowRightClick={this.onRightClick}
                stopPropagationOnClick={this.props.stopPropagationOnClick}
                rowReference={((doHighlight) ? this.highlightRowRef : null)}
                onDelete={this.onDelete}
                disabled={(this.state as ReactHierarchicalListState).disabled}
                addDeleteButton={this.props.addDeleteButton}
                addSortIcon={this.props.addSortIcon && ((null != row.allowDrag) ? row.allowDrag : false)}
                addDropIcon={this.props.addDropIcon && ((null != row.allowDrop) ? row.allowDrop : false)}
                deleteButtonTabIndex={this.props.deleteButtonTabIndex}
                deleteButtonID={this.props.deleteButtonID}
                shouldRowUpdate={this.props.shouldRowUpdate}
            />;
        }
    }

    getRowContent(row: any, hierarchyIndex: number, highlightSelectedRow: any):any {
        if(null != this.props.contentOverride) {
            return this.props.contentOverride(row, hierarchyIndex, (this.state as ReactHierarchicalListState).disabled);
        } else if (null != this.props.hierarchyKey && null != row[this.props.hierarchyKey] && (row[this.props.hierarchyKey] as any[]).length > 0) {
            return (row[this.props.hierarchyKey] as any[]).map((subRow, subIndex) => {
                return this.getAccordionRow(subRow, highlightSelectedRow, false);
            })
        } else if(this.props.enableUnboundDragAndDrop) {
            return this.getAccordionRow({isHiddenDragRow: true, hiddenParentRow: row}, highlightSelectedRow, true);
        } else {
            return "";
        }
    }

    getAccordionRow(row: any, highlightSelectedRow: any, isHiddenDragRow: boolean) {
        if(!isHiddenDragRow) {
            this.totalRows.push(row);
        }
        const hierarchyIndex = this.totalRows.length - 1;

        const newID = this.props.id + '_' + hierarchyIndex;
        const newExpandedID = this.props.expandedID + '_' + hierarchyIndex;

        if(this.props.enableDragAndDrop) {
            if(null != this.props.allowDrag) {
                this.foundParentRow = false;
                if(isHiddenDragRow) {
                    row.allowDrag = this.props.allowDrag(null, row.hiddenParentRow);
                } else {
                    this.foundParentRow = false;
                    const parentRow = this.getParentRow((this.state as ReactHierarchicalListState).rows, row, null);
                    row.allowDrag = this.props.allowDrag(row, parentRow);
                }
            } else if(null != row && null == row.allowDrag) {
                row.allowDrag = true;
            }

            if(null != this.props.allowDrop) {
                this.foundParentRow = false;
                if(isHiddenDragRow) {
                    row.allowDrop = this.props.allowDrop(null, row.hiddenParentRow);
                } else {
                    this.foundParentRow = false;
                    const parentRow = this.getParentRow((this.state as ReactHierarchicalListState).rows, row, null);
                    row.allowDrop = this.props.allowDrop(row, parentRow);
                }
            } else if(null != row && null == row.allowDrop) {
                row.allowDrop = true;
            }
        }

        if(null != this.props.allowExpandAndCollapse) {
            row.allowExpandAndCollapse = this.props.allowExpandAndCollapse(row, hierarchyIndex);

        } else if(null != row) {
            row.allowExpandAndCollapse = true;
        }

        const setProps = {
            id: newID,
            shellID: hierarchyIndex + '',
            text: this.getRowDisplay(row, hierarchyIndex, isHiddenDragRow),
            content: (isHiddenDragRow) ? "" : this.getRowContent(row, hierarchyIndex, highlightSelectedRow),
            collapsedStyle: this.props.collapsedStyle,
            collapsedCSS: this.props.collapsedCSS,
            css: ((this.state as ReactHierarchicalListState).rowIsDragging && (this.state as ReactHierarchicalListState).highlightSelectedRow == row) ? 'react-drag' : '',
            expandedCSS: this.props.expandedCSS,
            expandedStyle: this.props.expandedStyle,
            contentStyle: (null != this.props.contentStyle) ? this.props.contentStyle(row, hierarchyIndex) : {marginTop: '0px', paddingLeft: '16px', fontWeight: "normal"},
            contentCSS: this.props.contentCSS,
            dataKey: this.props.dataKey,
            row: row,
            rowIndex: hierarchyIndex,
            onClick: this.onClick,
            onRefresh: this.props.onRefresh,
            expandedID: newExpandedID,
            stopPropagationOnClick: this.props.stopPropagationOnClick,
            disableID: this.props.disableID + '_' + hierarchyIndex,
            disabled: (this.state as ReactHierarchicalListState).disabled,
            arrowExpandCSS: this.props.arrowExpandCSS,
            arrowCollapseCSS: this.props.arrowCollapseCSS,
            arrowStyle: (null != this.props.arrowStyle) ? this.props.arrowStyle(row, hierarchyIndex) : {width: "16px", marginTop: "1px"},
            enableDragAndDrop: ((null != row.allowDrag) ? row.allowDrag : false) || ((null != row.allowDrop) ? row.allowDrop : false),
            onDragStart: this.onDragStartHandler,
            onDragStop: this.onDrop,
            onDuringDrag: this.onDragHandler,
            allowExpandAndCollapse: (null != row.allowExpandAndCollapse) ? row.allowExpandAndCollapse : true,
        };
        return getReactAccordion(setProps);
    }

    getAllowExpandAndCollapse(row: any) {
        if(null != row) {
            if(null != row.allowExpandAndCollapse && undefined != row.allowExpandAndCollapse) {
                return row.allowExpandAndCollapse;
            }
        }
        return true;
    }

    onDisableClick(event: any) {
        const isChecked = $(event.currentTarget).is(":checked");
        this.setState({
            disabled: isChecked
        });
    }

    getTotal(rows: any[]) {
        let count = 0;
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (null != this.props.hierarchyKey && null != row[this.props.hierarchyKey] && (row[this.props.hierarchyKey] as any[]).length > 0) {
                count = count + this.getTotal(row[this.props.hierarchyKey]);
            }
            count = count + 1;
        }
        return count;
    }

    render() {
        this.totalRows = [];
        this.foundPrevRow = false;
        this.returnNextRow = false;
        this.foundParentRow = false;
        let first = 0;

        return <div id={this.props.id} className={this.props.css} style={this.props.style} onKeyDown={this.onKeyDown} tabIndex={this.props.tabIndex}>
            <div className={this.props.divContainerCSS} style={this.props.divContainerStyle}>
                {<table className="categoryAllHide">
                    <tr>
                        <td><input id={this.props.totalCountID} value={this.getTotal((this.state as ReactHierarchicalListState).rows)} autoComplete="off"/>
                        </td>
                        <td>
                            HIDDEN TOTAL COUNT
                        </td>
                    </tr><tr>
                    <td><input
                        id={this.props.disableID}
                        type="checkbox"
                        onChange={this.onDisableClick}
                        checked={(this.state as ReactHierarchicalListState).disabled}
                    /></td>
                    <td>
                        HIDDEN DISABLE BUTTON
                    </td>
                </tr>
                </table>
                }
                {getReactButton({id: this.props.refreshID, css: "categoryAllHide", onClick: this.onRefresh, text: "HIDDEN REFRESH BUTTON"})}
                {(this.state as ReactHierarchicalListState).rows.map((row) => {
                    return this.getAccordionRow(row, (this.state as ReactHierarchicalListState).highlightSelectedRow, false);
                })}
                {
                    this.getAccordionRow({isBottomRow: true, isHiddenDragRow: true, hiddenParentRow: null}, (this.state as ReactHierarchicalListState).highlightSelectedRow, true)
                }
            </div>
        </div>;
    }
}
